import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { DropZone, IconButton } from '../ui';

export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024;

/** Why a file cannot be the screenshot, or '' when it can. */
export const screenshotProblem = (file) => {
  if (!file) return '';
  if (!SCREENSHOT_TYPES.includes(file.type)) return 'Attach a PNG, JPG, WebP or GIF image.';
  if (file.size > SCREENSHOT_MAX_BYTES) return 'That image is over 10 MB — crop it or save it as a JPG.';
  return '';
};

const sizeLabel = (bytes) => (bytes < 1048576 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`);

/**
 * One optional screenshot: browse, drop, or paste (the parent forwards a
 * pasted image through `onChange`). Once chosen it shows as a thumbnail with
 * its name and a remove button. Validation is the parent's: it calls
 * `screenshotProblem` and decides where the message goes.
 *
 * Props:
 *   file      — the chosen File, or null
 *   onChange  — (File | null) => void
 *   describedBy — id of the field's hint/error, for the drop target
 */
const ScreenshotField = ({ file, onChange, describedBy }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState('');

  useEffect(() => {
    if (!file) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (file) {
    return (
      <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-2">
        {preview && (
          <img
            src={preview}
            alt="Screenshot to attach"
            className="tw-h-14 tw-w-20 tw-shrink-0 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-object-cover"
          />
        )}
        <div className="tw-min-w-0 tw-flex-1">
          <p className="tw-m-0 tw-truncate tw-text-[0.8125rem] tw-font-medium tw-text-content" title={file.name}>{file.name}</p>
          <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{sizeLabel(file.size)}</p>
        </div>
        <IconButton label="Remove screenshot" icon={Trash2} size="sm" variant="ghost-destructive" onClick={() => onChange(null)} />
      </div>
    );
  }

  return (
    <>
      <DropZone
        size="sm"
        state={dragging ? 'active' : 'idle'}
        aria-describedby={describedBy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const dropped = e.dataTransfer?.files?.[0];
          if (dropped) onChange(dropped);
        }}
      >
        <ImagePlus size={18} aria-hidden="true" className="tw-shrink-0 tw-text-content-subtle group-hover:tw-text-primary" />
        <span className="tw-text-[0.8125rem] tw-text-content-muted">
          <span className="tw-font-semibold tw-text-primary">Add a screenshot</span> — drop it here, paste it, or browse
        </span>
      </DropZone>
      <input
        ref={inputRef}
        type="file"
        accept={SCREENSHOT_TYPES.join(',')}
        className="tw-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          if (picked) onChange(picked);
        }}
      />
    </>
  );
};

export default ScreenshotField;
